import { useState, useEffect, useContext, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import { Spinner } from 't-lib-ui/widgets';
import { AppState } from '../context/general';
import { getPublicFeed } from '../connector/api';
import { markAnonFromShare, applyFirstFeedDefaults } from '../connector/anon-onboarding';
import { getSinglePost } from '../connector/post-engine';
import { RestCatalog, RestUserFeed } from '../schema-copy/api-schema';
import type { Post } from '../schema-copy/feed-schema';
import { formatPublishedTime, parseAuthor } from '../utils/formatting';
import ShareHeader from '../components/ShareHeader';
import { useReport } from '../components/ReportPopup';
import LegalFooter from '../components/LegalFooter';
import { loadExternalScripts } from '../components/external-scripts';
import { hideGhostEmbedFallbackImages, hydrateDigestPostEmbeds, hydrateImageOnlyPost, hydrateInstagramEmbeds, hydrateTikTokEmbeds, hydrateThreadsEmbeds, isPostImageInBody, normalizeAsides, collapseBrRuns, dedupeAdjacentImages, hydrateAttributedQuotes, hydrateUnsupportedCharts, sanitizeHtmlClient } from '../components/post-content';
import { EmbedConsentApi, gateEmbeds, neutralizeGatedIframesInHtml } from '../components/embed-consent';
import { ensureSiteCss, postSiteClass } from '../components/site-css';
import type { EmbedService } from '../schema-copy/api-schema';
import './SharePage.css';
import '../components/post-tailwind.css';

// Shows the author's real name by default; if an email is also known, clicking toggles to
// "Name <email>" and back.
const PostByline: React.FC<{ author: string }> = ({ author }) => {
  const [showFull, setShowFull] = useState(false);
  const { name, email } = parseAuthor(author);
  const toggleable = !!(name && email);
  const text = !toggleable ? (name ?? email ?? author) : showFull ? `${name} <${email}>` : name!;
  return (
    <div className="postByline">
      By <span
        className={`postAuthor${toggleable ? ' postAuthorToggle' : ''}`}
        title={toggleable ? 'Click to show the email address' : undefined}
        onClick={toggleable ? () => setShowFull(v => !v) : undefined}
      >{text}</span>
    </div>
  );
};

const SharePostPage: React.FC = () => {
  const { f: feedId, i: postIndexStr } = useParams<{ f: string; i: string }>();
  const postIndex = parseInt(postIndexStr || '0', 10);
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  const auth = appState.state.authData;
  const [feed, setFeed] = useState<RestUserFeed | RestCatalog | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [trying, setTrying] = useState(false);
  const { report, reportPopup } = useReport();

  // On the share page, embed consent is always required, per page view — never persisted
  // and independent of preferences/jurisdiction.
  const sessionConsented = useRef(new Set<EmbedService>());
  const sessionConsent = useMemo<EmbedConsentApi>(() => ({
    isAllowed: s => sessionConsented.current.has(s),
    grant: s => { sessionConsented.current.add(s); },
  }), []);

  useEffect(() => {
    if (!feedId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const feedData = await getPublicFeed(feedId, auth);
        if (cancelled) return;
        ensureSiteCss(feedData);
        setFeed(feedData);

        const postData = await getSinglePost({ ...feedData, postCount: feedData.postCount }, postIndex);
        if (cancelled) return;
        setPost(postData);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [feedId, postIndex, auth]);

  const feedName = feed?.shortTitle || feed?.title || 'this feed';
  const feedLink = feed ? `/feed/${feed.id}` : '#';
  const siteClass = postSiteClass(feed?.siteUrl);

  // "Try now": no account needed — follow this feed in the active (anon) engine and drop straight
  // into the reader so the visitor can read the whole feed immediately.
  const handleTryNow = async () => {
    if (!feed || trying) return;
    setTrying(true);
    try {
      feedEngine.registerFeedInfo(feed);
      await feedEngine.subscribe(feed.id);
      applyFirstFeedDefaults(feedEngine, feed.id);
      markAnonFromShare();
      goTo(routerCtx, `/a/feed/${feed.id}`);
    } catch (e) {
      console.error('Try now failed:', e);
      setTrying(false);
    }
  };

  // Replace siteUrl links with /feed/{feedId} links in post body
  const processPostBody = (html: string): string => {
    if (!feed?.siteUrl) return html;
    // Replace links to the feed's site with links to the feed page
    const siteHost = new URL(feed.siteUrl).hostname;
    return html.replace(
      new RegExp(`href=["'](https?://(www\\.)?${siteHost.replace(/\./g, '\\.')})/?["']`, 'gi'),
      `href="${feedLink}"`
    );
  };

  // dangerouslySetInnerHTML attaches iframes to the live document before any effect can run,
  // so unconsented third-party iframe srcs must be stripped from the HTML string itself.
  // sessionConsented starts empty on every page view, so all gated iframes are neutralized.
  // sanitizeHtmlClient adds the same client-side defense-in-depth over the server-side
  // sanitizer that safeSetHTML applies in the reader (this page bypasses safeSetHTML).
  const bodyHtml = useMemo(
    () => post ? neutralizeGatedIframesInHtml(sanitizeHtmlClient(processPostBody(post.description || '')), sessionConsent.isAllowed) : '',
    [post, feed, sessionConsent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImgError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.referrerPolicy != 'no-referrer') {
      img.referrerPolicy = 'no-referrer';
      img.src = img.src.trim(); // dummy op for reloading
    } else
      img.style.display = 'none';
  }, []);

  const bodyRef = useRef<HTMLDivElement>(null);
  const rehydrateEmbeds = useCallback(() => {
    if (!bodyRef.current) return;
    loadExternalScripts(post?.externalScripts, bodyRef.current, sessionConsent.isAllowed);
  }, [post?.externalScripts, sessionConsent]);

  useLayoutEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.querySelectorAll('img').forEach(img => {
      img.onerror = () => {
        if (img.referrerPolicy != 'no-referrer') {
          img.referrerPolicy = 'no-referrer';
          img.src = img.src.trim(); // dummy op for reloading
        } else
          img.style.display = 'none';
      };
    });
    // This page renders the body via dangerouslySetInnerHTML rather than the
    // post-dom-factory pipeline, so run the same embed hydrators the reader does
    // before loadExternalScripts hands off to the embed scripts.
    dedupeAdjacentImages(bodyRef.current);
    hideGhostEmbedFallbackImages(bodyRef.current);
    normalizeAsides(bodyRef.current);
    collapseBrRuns(bodyRef.current);
    hydrateAttributedQuotes(bodyRef.current);
    hydrateUnsupportedCharts(bodyRef.current);
    hydrateDigestPostEmbeds(bodyRef.current);
    hydrateImageOnlyPost(bodyRef.current);
    hydrateInstagramEmbeds(bodyRef.current);
    hydrateTikTokEmbeds(bodyRef.current);
    hydrateThreadsEmbeds(bodyRef.current);
    // Overlay unconsented embeds: iframes were already neutralized pre-render (see bodyHtml),
    // vendor scripts are blocked in loadExternalScripts until the overlay is clicked.
    gateEmbeds(bodyRef.current, sessionConsent);
    rehydrateEmbeds();
  }, [post, rehydrateEmbeds, sessionConsent]);

  useEffect(() => {
    if (!post?.externalScripts?.includes('x-widget')) return;

    const rehydrateWhenVisible = () => {
      if (!document.hidden)
        rehydrateEmbeds();
    };

    window.addEventListener('pageshow', rehydrateWhenVisible);
    document.addEventListener('visibilitychange', rehydrateWhenVisible);

    return () => {
      window.removeEventListener('pageshow', rehydrateWhenVisible);
      document.removeEventListener('visibilitychange', rehydrateWhenVisible);
    };
  }, [post?.externalScripts, rehydrateEmbeds]);

  return (
    <div className="sharePage">
      <ShareHeader />
      {reportPopup}
      <div className="sharePageContent">
        {loading && (
          <div className="sharePageLoading">
            <Spinner spinning />
            <span>Loading post...</span>
          </div>
        )}

        {error && (
          <div className="sharePageError">
            <h2>Post not found</h2>
            <p>{error}</p>
            <a href="https://downread.com" className="shareBannerCta">Discover feeds on Downread</a>
          </div>
        )}

        {feed && post && !loading && (
          <>
            <div className="shareBanner">
              <div className="shareBannerText">
                You're reading a post from <a href={feedLink}><strong>{feedName}</strong></a> on{' '}
                <a href="https://downread.com">Downread</a>.
                Try now to read the whole feed. <strong>No account needed.</strong>
              </div>
              <button type="button" className="shareBannerCta" onClick={handleTryNow} disabled={trying}>
                {trying ? 'Opening…' : 'Try now'}
              </button>
            </div>

            <div className={`sharePostCard${siteClass ? ` ${siteClass}` : ''}`}>
              <div className="postHeader">
                <div className="postMeta">
                  <a href={feedLink} className="postFeed">{feedName}</a>
                  <span className="postDivider">&bull;</span>
                  <span className="postTime">{post.pubDate ? formatPublishedTime(post.pubDate) : ''}</span>
                </div>
              </div>

              <h1 className="postTitle">
                {post.link ? (
                  <a href={post.link} target="_blank" rel="noopener noreferrer">{post.title || '(No title)'}</a>
                ) : (
                  post.title || '(No title)'
                )}
              </h1>

              {post.author && <PostByline author={post.author} />}

              {post.imageUrl && !isPostImageInBody(post.imageUrl, post.description) && (
                <div className="postImage">
                  <img src={post.imageUrl} alt={post.title || ''} onError={handleImgError} />
                </div>
              )}

              <div
                ref={bodyRef}
                className="postBody"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />

              {post.link && (
                <div className="sharePostActionsRow">
                  <div className="sharePostSeparatorActions">
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sharePostSeparatorButton"
                      title="View original post"
                    >
                      <span className="sharePostSeparatorIcon">&#8599;</span>
                      <span className="sharePostSeparatorLabel">Original Post</span>
                    </a>
                  </div>
                </div>
              )}

              <div className="sharePostReportRow" style={{ padding: '0 24px 20px', textAlign: 'right' }}>
                <button
                  type="button"
                  className="sharePostReportLink"
                  onClick={() => report({ feedId: feed.id, postIndex, title: post.title || undefined })}
                  style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: '0.85em', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Report this post
                </button>
              </div>
            </div>

            <div className="shareBanner bottom">
              <div className="shareBannerText">
                There are more posts from <a href={feedLink}><strong>{feedName}</strong></a>.
                Browse the full feed on <a href="https://downread.com">Downread</a>.
                {feed.copyright && (
                  <span className="shareBannerCopyright">{/copyright/i.test(feed.copyright) ? '' : 'Copyright: '}{feed.copyright}</span>
                )}
              </div>
              <a href={feedLink} className="shareBannerCta">View more posts</a>
            </div>
          </>
        )}
        <LegalFooter bottom />
      </div>
    </div>
  );
};

export default SharePostPage;
